import { AxiosError } from 'axios'
import { useSnackbarAlertContext } from 'contexts/SnackbarContext'
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import {
  deleteDocumentApprovalFile,
  getDocumentApprovalFile,
  getListDocuments,
  putDocumentApproval
} from 'services/documentos/documentApproval'
import useDocumentsManagementStore from 'store/useDocumentsManagementStore'
import useSidebarStore from 'store/useSidebar'
import useUserInfoStore from 'store/useUser'
import { ErrorResponse, HandlerErrorMessage } from 'utils/HandlerErrorMessage'
import changeFilterToString from 'utils/changeFilterToString'
import { documentsSidebarIds } from 'pages/documentManagement/sidebars/SidebarConfig'

export const useDocumentManagementHook = () => {
  const { showAlert } = useSnackbarAlertContext()
  const { openSidebar, closeSidebar } = useSidebarStore((state) => state)
  const { setProposalsData, stage, setProduct, clearStage, currentFilter } =
    useDocumentsManagementStore((state) => state)
  const [isSaveLoading, setIsSaveLoading] = useState(false)
  const { privilegios } = useUserInfoStore((state) => state)

  const getListDocumentsMutation = useMutation({
    mutationFn: getListDocuments,
    onSuccess: (data) => {
      if (data.length === 0) {
        openSidebar(documentsSidebarIds.FILTRO)
      }

      setProposalsData(data)
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      HandlerErrorMessage({ showAlert, error: error })
    }
  })

  const handleCallGetListDocuments = async (idDocumento?: number) => {
    // Função para formatar data sem timezone offset
    const formatLocalDateToISO = (date: Date, isEndOfDay = false) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')

      if (isEndOfDay) {
        return `${year}-${month}-${day}T23:59:59.995Z`
      } else {
        return `${year}-${month}-${day}T00:00:00.000Z`
      }
    }

    const startDate = new Date(currentFilter.dt_venda.dt_venda_inicial)
    const endDate = new Date(currentFilter.dt_venda.dt_venda_final)

    const filter = {
      id_documento_inicial: currentFilter.id_documento?.id_documento_inicial ?? '',
      id_documento_final: currentFilter.id_documento?.id_documento_final ?? '',
      st_status: currentFilter.st_status,
      dt_venda_inicial: formatLocalDateToISO(startDate, false),
      dt_venda_final: formatLocalDateToISO(endDate, true),
      cd_inscricao_nacional: currentFilter.cd_inscricao_nacional,
      cpf_cnpj_comissionado: currentFilter.cpf_cnpj_comissionado
    }

    getListDocumentsMutation.mutate(changeFilterToString(filter), {
      onSuccess: (data) => {
        if (idDocumento && data) {
          const newProposal = data.find((prop) => prop.id_documento === idDocumento)
          if (!newProposal) {
            return closeSidebar()
          }
          setProduct(newProposal)
        }
      },
      onError: (error: AxiosError<ErrorResponse>) => {
        HandlerErrorMessage({ showAlert, error })
      }
    })
  }

  const { isPending: isLoading } = getListDocumentsMutation

  const deleteDocumentApprovalFileMutation = useMutation({
    mutationFn: deleteDocumentApprovalFile,
    onError: (error: AxiosError<ErrorResponse>) => {
      HandlerErrorMessage({ showAlert, error: error })
    },
    retry: 0
  })

  const putDocumentApprovalMutation = useMutation({
    mutationFn: putDocumentApproval,
    onError: (error: AxiosError<ErrorResponse>) => {
      HandlerErrorMessage({ showAlert, error: error })
    },
    retry: 0
  })

  const saveUpdateMutation = useMutation({
    mutationFn: async (idDocumento: number) => {
      if (privilegios.sn_documentacoes_altera === 'N') {
        throw new Error('Este usuário não tem permissão para realizar esta ação')
      }

      const { delete: deleteArray, edit: editArray } = stage

      for (const payload of deleteArray) {
        await deleteDocumentApprovalFileMutation.mutateAsync(payload)
      }

      if (editArray.length > 0) {
        await putDocumentApprovalMutation.mutateAsync(editArray)
      }

      return idDocumento
    },
    onSuccess: async (idDocumento) => {
      await handleCallGetListDocuments(idDocumento)
      clearStage()
    },
    onError: (error: Error | AxiosError<ErrorResponse>) => {
      const message = error instanceof Error ? error.message : 'Erro ao salvar alterações'
      HandlerErrorMessage({
        showAlert,
        error: error instanceof AxiosError ? error : undefined,
        customErrorMessage: message
      })
    },
    onSettled: () => {
      setIsSaveLoading(false)
    }
  })

  const handlerSaveUpdate = (idDocumento: number) => {
    setIsSaveLoading(true)
    saveUpdateMutation.mutate(idDocumento)
  }

  const getDocumentApprovalFileMutation = useMutation({
    mutationFn: getDocumentApprovalFile,
    onError: (error: AxiosError<ErrorResponse>) => {
      HandlerErrorMessage({ showAlert, error: error })
    }
  })

  return {
    handlerSaveUpdate,
    isLoading,
    getListDocumentsMutation,
    getDocumentApprovalFileMutation,
    isSaveLoading,
    handleCallGetListDocuments
  }
}
